// client/src/components/configuracoes/integracoes/IntegracoesConfigPage.js
import React, { Fragment, useState } from 'react';
import { Key, MessageCircle, Mail, Smartphone, Coins, Loader2, ShoppingCart } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import IntegracaoCanalCard from './IntegracaoCanalCard';
import ComprarCreditoModal from './ComprarCreditoModal';
import { useAuth } from '../../../context/auth/AuthProvider';
import { useSaldoCredito, useExtratoCredito } from '../../../hooks/useComunicacaoCredito';

// ✅ Cada canal tem uma lista de provedores suportados (ver
// PROVEDORES_POR_CANAL/CAMPOS_OBRIGATORIOS em
// server/controllers/integracaoCredencialController.js). Canais com um só
// provedor não mostram seletor; 'email' tem dois (SendGrid ou SMTP
// genérico — Google Workspace/Gmail, Locaweb, qualquer outro) e 'whatsapp'
// tem dois (Meta Business, oficial, ou Evolution API, não-oficial — número
// próprio via QR Code, disponível pra Conta, Franquia e Matriz como
// fallback de segurança) — exibe um <select> em
// IntegracaoCanalCard pra escolher entre eles.
//
// evolution_api tem `campos: []` de propósito — não pede nenhum dado ao
// tenant, a conexão é 100% via QR Code (IntegracaoCanalCard troca o
// formulário por um botão "Conectar" nesse caso).
const CANAIS = [
    {
        canal: 'whatsapp',
        label: 'WhatsApp',
        icon: MessageCircle,
        placeholderRemetente: 'Ex.: Atendimento Onmicare',
        provedores: [
            {
                value: 'meta_business',
                label: 'Meta Business API (oficial)',
                campos: [
                    { name: 'accessToken', label: 'Access Token', type: 'password' },
                    { name: 'phoneNumberId', label: 'Phone Number ID', type: 'text' },
                ],
            },
            {
                value: 'evolution_api',
                label: 'WhatsApp Web — QR Code',
                campos: [],
            },
        ],
    },
    {
        canal: 'email',
        label: 'E-mail',
        icon: Mail,
        placeholderRemetente: 'Ex.: Equipe Onmicare',
        provedores: [
            {
                value: 'sendgrid',
                label: 'SendGrid',
                campos: [
                    { name: 'apiKey', label: 'API Key', type: 'password' },
                    { name: 'fromEmail', label: 'E-mail do Remetente', type: 'email' },
                    { name: 'fromName', label: 'Nome do Remetente', type: 'text' },
                ],
            },
            {
                value: 'smtp_custom',
                label: 'SMTP Personalizado (Google Workspace, Locaweb, etc.)',
                campos: [
                    { name: 'host', label: 'Servidor SMTP (host)', type: 'text', placeholder: 'Ex.: smtp.gmail.com' },
                    { name: 'port', label: 'Porta', type: 'text', placeholder: 'Ex.: 587' },
                    { name: 'user', label: 'Usuário', type: 'text', placeholder: 'Ex.: contato@suaempresa.com.br' },
                    { name: 'password', label: 'Senha', type: 'password' },
                    { name: 'fromEmail', label: 'E-mail do Remetente', type: 'email' },
                    { name: 'fromName', label: 'Nome do Remetente', type: 'text' },
                ],
            },
        ],
    },
    {
        canal: 'sms',
        label: 'SMS',
        icon: Smartphone,
        placeholderRemetente: 'Ex.: Onmicare',
        provedores: [
            {
                value: 'twilio',
                label: 'Twilio',
                campos: [
                    { name: 'accountSid', label: 'Account SID', type: 'text' },
                    { name: 'authToken', label: 'Auth Token', type: 'password' },
                    { name: 'fromNumber', label: 'Número de Origem', type: 'text' },
                ],
            },
        ],
    },
];

const STATUS_LABEL_EXTRATO = { compra: 'Compra', consumo: 'Consumo', ajuste: 'Ajuste' };

// ✅ Créditos de Comunicação — só faz sentido pra Conta (é ela quem
// consome, quando um envio cai no fallback da credencial da Matriz — ver
// comunicacaoDispatchService.js::enviarMensagemReal). Franquia/Matriz
// recebem o pagamento, não compram saldo pra si mesmas.
const CreditosComunicacaoSection = () => {
    const { data: saldoData, isLoading: isLoadingSaldo } = useSaldoCredito();
    const { data: extratoData, isLoading: isLoadingExtrato } = useExtratoCredito({ limite: 5 });
    const [modalAberto, setModalAberto] = useState(false);

    return (
        <div className="content-panel" style={{ marginBottom: '1rem' }}>
            <div className="form-section">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <h4 className="form-section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                        <Coins size={18} className="icon" />
                        Créditos de Comunicação
                    </h4>
                    <button type="button" className="action-button action-button-primary" onClick={() => setModalAberto(true)}>
                        <ShoppingCart size={18} className="icon" />
                        Comprar créditos
                    </button>
                </div>

                <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginTop: '0.5rem' }}>
                    Consumidos automaticamente só quando um envio usa a capacidade da Matriz como
                    reserva (sua credencial própria configurada acima nunca consome crédito).
                </p>

                <div style={{ marginTop: '0.75rem', fontSize: '1.5rem', fontWeight: 700 }}>
                    {isLoadingSaldo ? <Loader2 size={20} className="icon animate-spin" /> : `${saldoData?.saldo ?? 0} créditos`}
                </div>

                {!isLoadingExtrato && extratoData?.itens?.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                        <h5 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Últimas movimentações</h5>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                            {extratoData.itens.map((t) => (
                                <li key={t._id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                                    <span>{STATUS_LABEL_EXTRATO[t.tipo] || t.tipo} — {new Date(t.createdAt).toLocaleDateString('pt-BR')}</span>
                                    <strong style={{ color: t.quantidade >= 0 ? 'var(--color-success, #16a34a)' : 'var(--color-danger, #dc2626)' }}>
                                        {t.quantidade >= 0 ? '+' : ''}{t.quantidade}
                                    </strong>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {modalAberto && <ComprarCreditoModal onClose={() => setModalAberto(false)} />}
        </div>
    );
};

const IntegracoesConfigPage = () => {
    const { scope } = useAuth();

    // ✅ Evolution API (QR Code) disponível pra Conta, Franquia e Matriz —
    // todos os três escopos usam como opção de segurança/fallback caso a
    // integração direta com a Meta caia ou tenha problema, aproveitando a
    // cascata de resolução em tempo de envio (conta → franquia → matriz,
    // ver integracaoCredencialService.js::resolverCredencial) pra nunca
    // ficar sem conseguir mandar comunicação.
    const canais = CANAIS;

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Key />}
                    title="Integrações"
                    subtitle="Credenciais de envio para WhatsApp, e-mail e SMS"
                />
                {scope === 'conta' && <CreditosComunicacaoSection />}
                {canais.map((c) => (
                    <IntegracaoCanalCard key={c.canal} {...c} />
                ))}
            </div>
        </Fragment>
    );
};

export default IntegracoesConfigPage;
